import React from 'react';

import userHandledBackend, {UserInResponse} from '@lexian/api/user-api';
import {EFAutocompleteProps} from '_/utilities/form';

import {ActiveState} from '../ActiveFilter/ActiveFilter.types';
import {
  ActiveStateFilterState,
  useActiveStateFilter,
} from '../ActiveFilter/useActiveStateFilter';
import {
  AutocompleteBase,
  paginatedLoader,
} from '../AutocompleteBase/AutocompleteBase';

const activeStateToFlag = (activeState: ActiveState): boolean | undefined =>
  activeState === 'ACTIVE'
    ? true
    : activeState === 'INACTIVE'
      ? false
      : undefined;

type Props = Omit<EFAutocompleteProps, 'options'> & {
  onUserChange?: (user: UserInResponse | undefined) => void;
  excludeCurrentUser?: boolean;
  alreadySelectedUserIds?: string[];
  filterable?: boolean;
  searchFiltersPersistenceKey?: string;
  onSelectedUserIsFilteredOut?: () => void;
};

export function UserAutocomplete({
  onUserChange,
  excludeCurrentUser,
  alreadySelectedUserIds = [],
  filterable = false,
  searchFiltersPersistenceKey = 'user-autocomplete',
  onSelectedUserIsFilteredOut,
  ...rest
}: Props): React.ReactElement {
  const activeStateFilter = useActiveStateFilter(
    `userAutocomplete.searchFilters.${searchFiltersPersistenceKey}`
  );

  return (
    <AutocompleteBase
      {...rest}
      filter={filterable ? activeStateFilter : undefined}
      loadItems={paginatedLoader({
        endpointFunction: userHandledBackend.searchUsers,
        getPageItemFromResponse: (body) => body.users,
        params: (filterState: ActiveStateFilterState | undefined) => ({
          isActive: activeStateToFlag(filterState?.activeState ?? 'ACTIVE'),
          excludeCurrentUser: Boolean(excludeCurrentUser),
        }),
      })}
      toOption={(user) => ({
        value: user.id,
        displayText: user.displayName,
        disabled: alreadySelectedUserIds.includes(user.id),
      })}
      noOptionFiltering
      onItemChange={onUserChange}
      onSelectedItemMissing={
        filterable ? onSelectedUserIsFilteredOut : undefined
      }
    />
  );
}
